import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import dagre from "dagre";
import ReactFlow, { useEdgesState, useNodesState } from "reactflow";
import "reactflow/dist/style.css";
import useColors from "../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../api/hooks/useSmartAndRegularAssets";
import LoadingIcon from "../../../../components/LoadingIcon";
import ScenarioSetNode from "./ScenarioSetNode";
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 80;
const nodeHeight = 30;

const getLayoutedElements = (nodes, edges) => {
  // LR = Left to Right
  dagreGraph.setGraph({ rankdir: "LR" });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);

    if (nodeWithPosition) {
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    }
    if (nodeWithPosition) {
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    }

    return node;
  });
  return { nodes, edges };
};

const nodeTypes = { customNode: ScenarioSetNode };

const ScenarioSetAggregatedFlow = ({
  scenarioSet,
  width = null,
  height = null,
  setSelectedScenarioSet,
  fullScreen,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [preLayoutNodes, setPreLayoutNodes] = useState(null);
  const [nodesAreLoading, setNodesAreLoading] = useState(true);
  const [preLayoutEdges, setPreLayoutEdges] = useState(null);
  const paperWidth = width || 400;
  const paperHeight = fullScreen ? 160 : 110;
  const [loading, setLoading] = useState(true);
  const colors = useColors();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // It's required to solve ResizeObserver loop limit exceeded error
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 700);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Re-render flow when fullScreen state changes
  useEffect(() => {
    if (preLayoutNodes && preLayoutEdges) {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(preLayoutNodes, preLayoutEdges);

      const startAssetYValue = layoutedNodes[0]?.position?.y || 0;
      const newLayoutedNodes = layoutedNodes.map((node) => ({
        ...node,
        position: {
          x: node.position.x,
          y: startAssetYValue,
        },
      }));

      setNodes(newLayoutedNodes);
      setEdges(layoutedEdges);
    }
  }, [fullScreen]);

  const { regularAndActiveSmartAsset: assets, isLoadingAll: assetsIsLoading } =
    useSmartAndRegularAssets();

  const createMergedNodesFromLocationsPhasesAndAsset = (scenarioSet) => {
    const tempNodes = [];
    for (let i = 0; i < scenarioSet.locations_in_order.length; i++) {
      // node for location
      tempNodes.push({
        id: `${scenarioSet.locations_in_order[i]}-${i}`,
        type: "location",
        value: scenarioSet.locations_in_order[i],
        position: "middle",
      });

      // node for phase combo
      tempNodes.push({
        id: scenarioSet.phases_info[i]?.phase_category,
        type: "phase",
        position: "middle",
        value: {
          category: scenarioSet.phases_info[i]?.phase_category,
          number_of_phase_combos: scenarioSet.phases_info[i]?.number_of_phases,
          phase_ids: scenarioSet.phases_info[i]?.phase_ids,
        },
        start: scenarioSet.locations_in_order[i],
        end: scenarioSet.locations_in_order[i + 1],
        graphSource: "scenarioSetTable",
      });
    }

    // set first location node to start
    tempNodes[0].position = "start";

    const asset = assets?.filter((asset) => {
      return asset.asset_id === scenarioSet.asset_id;
    })[0];

    // node for asset
    tempNodes.push({
      id: scenarioSet.asset_id,
      type: "asset",
      value: {
        name: asset?.name,
        type: asset?.type,
        id: scenarioSet.asset_id,
      },
      position: "end",
    });

    return tempNodes;
  };

  const nodesAndEdgesGetter = () => {
    const nodesAndEdges = { nodes: [], edges: [] };
    const mergedNodes =
      createMergedNodesFromLocationsPhasesAndAsset(scenarioSet);
    const scenarioSetNodes = mergedNodes.map((node) => ({
      id: node.id,
      data: {
        nodeLocation: node.position,
        nodeValue: node.value,
        type: node.type,
        start: node.start,
        end: node.end,
      },
    }));

    const scenarioSetEdges = [];
    // for every step in the phase, create an edge that connects it to the next step
    for (let i = 0; i < mergedNodes.length - 1; i++) {
      const location = mergedNodes[i].id;
      const nextLocation = mergedNodes[i + 1].id;
      const edge = {
        id: `e${location}-${nextLocation}`,
        source: location?.toString(),
        target: nextLocation?.toString(),
      };
      scenarioSetEdges.push(edge);
    }

    nodesAndEdges.nodes = scenarioSetNodes;
    nodesAndEdges.edges = scenarioSetEdges;
    return nodesAndEdges;
  };

  useEffect(() => {
    if (scenarioSet && !assetsIsLoading) {
      if (!assets) return;
      const result = nodesAndEdgesGetter();
      const nodesWithTypes = result.nodes.map((node) => ({
        ...node,
        type: "customNode",
      }));
      setPreLayoutNodes(nodesWithTypes);

      const edgesWithTypes = result.edges.map((edge) => ({
        ...edge,
        style: {
          stroke: colors.grey_300,
          strokeWidth: 1,
        },
        type: "straight",
      }));
      setPreLayoutEdges(edgesWithTypes);
    }
    setNodesAreLoading(false);
  }, [scenarioSet, assetsIsLoading, assets]);

  useEffect(() => {
    if (preLayoutNodes && preLayoutEdges) {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(preLayoutNodes, preLayoutEdges);

      const startAssetYValue = layoutedNodes[0]?.position?.y || 0;
      const newLayoutedNodes = layoutedNodes.map((node) => ({
        ...node,
        position: {
          x: node.position.x,
          y: startAssetYValue,
        },
      }));

      setNodes(newLayoutedNodes);
      setEdges(layoutedEdges);
    }
  }, [nodesAreLoading]);

  const handleNodeClick = (event, node) => {
    if (
      node.data.type === "phase" ||
      node.data.type === "location" ||
      node.data.type === "asset"
    ) {
      // open a modal with the full scenario set graph
      const params = new URLSearchParams(location.search);
      const scenarioSetId = encodeURIComponent(
        scenarioSet.locations_and_phases_and_asset
      );
      params.set("selected_scenario_set", scenarioSetId);
      setSearchParams(params.toString());

      setSelectedScenarioSet(scenarioSet);
    }
  };

  return (
    <>
      {/* React flow requires wrapper object with width and height */}
      {nodes && nodes.length !== 0 ? (
        <Box
          sx={{
            width: paperWidth,
            height: paperHeight,
            border: "1px solid " + colors.divider2,
            backgroundColor: colors.grey_50,
            borderRadius: "12px",
            position: "relative", // Add position relative
          }}
        >
          {loading ? (
            <LoadingIcon />
          ) : (
            <>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodeClick={handleNodeClick}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                fitView
                zoomOnScroll={false}
                panOnScroll={false}
                preventScrolling={false}
                selectionOnDrag={false}
                proOptions={{ hideAttribution: true }}
                key={`flow-${fullScreen}`}
              />
            </>
          )}
        </Box>
      ) : (
        <LoadingIcon />
      )}
    </>
  );
};

export default ScenarioSetAggregatedFlow;
